import React, { useState, useEffect, Suspense } from "react";
import { Helmet } from "react-helmet";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import { Link } from "react-router-dom";
import styled from "styled-components";
import ImgBlogHero from "@assets/images/blog-2023/blog-post-hero.jpg";
import { useParams } from "react-router-dom";
import Blog from "@helpers/blog";
import ImgBlogPostThumb from "@assets/images/blog-2023/blog-post-thumb-min.jpg";

import { SiteHero } from "@src/styled/static";
import DoctorListComp from "@containers/newHome/doctor_list";
import LoadingLanding from "@containers/app/domains/docto.com/loading_landing";

const BlogPost = () => {
  const [post, setPost] = useState();
  const [relatedPosts, setRelatedPosts] = useState([]);
  const { slug } = useParams();

  useEffect(() => {
    const fetchPost = async () => {
      const thisPost = await Blog.getBlogPostBySlug(slug);
      await Blog.logView(slug);
      if (thisPost) {
        setPost(thisPost.post);
        setRelatedPosts(thisPost.relatedPosts);
      }
    };
    fetchPost();
  }, [slug]);

  if (!post) {
    return <LoadingLanding />;
  }

  return (
    <Suspense fallback={<LoadingLanding />}>
      <div>
        <Helmet>
          <title>Docto Blog</title>
          <meta
            name="description"
            content="DOCTO, the online hospital provides accelerated access to Australian Medical Specialists. Instant online consultations and specialist by appointment."
          />
          <link
            rel="canonical"
            href={`https://www.docto.com.au/blog/${post?.slug}`}
          />
        </Helmet>
        <LazyLoadComponent>
          <SiteHeroBlog
            SM={post?.featuredImage ? post?.featuredImage["275"] : undefined}
            MD={post?.featuredImage ? post?.featuredImage["768"] : undefined}
            LG={post?.featuredImage ? post?.featuredImage["1400"] : undefined}
          >
            <SiteHeroInner>
              <div className="container">
                <Link className="back-link" to="/blog">
                  &#60; Back
                </Link>

                <div className="row row-flex">
                  <div className=" col-sm-12 col-md-10 col-lg-6">
                    <h1>{post?.title}</h1>
                    <b>AUTHOR:</b> {post?.author}
                  </div>
                </div>
              </div>
            </SiteHeroInner>
          </SiteHeroBlog>
        </LazyLoadComponent>

        <BlogPostBody>
          <div className="container">
            <div className="row row-flex">
              <div
                className="blog-post-inner col-md-12 col-lg-9"
                dangerouslySetInnerHTML={{ __html: post?.content }}
              />
              <div className="col-md-12 col-lg-3">
                <SBRelated>
                  <span className="header">RELATED ARTICLES</span>
                  {relatedPosts.map((related) => {
                    return (
                      <BlogPostGridItem
                        key={related?.id}
                        to={`/blog/${related.slug}`}
                      >
                        <BlogPostThumb src={related?.featuredImage["275"]}>
                          <BlogPostTags>{related?.tags.join(",")}</BlogPostTags>
                        </BlogPostThumb>
                        <BlogPostContent>
                          <h4>{related.title}</h4>
                        </BlogPostContent>
                        <BlogPostFooter>READ ARTICLE</BlogPostFooter>
                      </BlogPostGridItem>
                    );
                  })}
                </SBRelated>

                <SBWidget>
                  <span className="header">Docto Tags</span>

                  <ul className="tags">
                    {post?.tags &&
                      Object.values(post?.tags).map((tag, index) => {
                        return (
                          <li key={`tag-${index}`}>
                            <Link
                              className="link"
                              to={`/blog/search?&tags=${tag}`}
                            >
                              {tag}
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </SBWidget>
                <SBWidget>
                  <span className="header">Docto Categories</span>

                  <ul className="tags">
                    {post?.categories &&
                      Object.values(post?.categories).map((tag, index) => {
                        return (
                          <li key={`tag-${index}`}>
                            <Link
                              className="link"
                              to={`/blog/search?&categories=${tag}`}
                            >
                              {tag}
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </SBWidget>
                <DoctorListCompStyled>
                  <DoctorListComp
                    headerComponent={
                      <DoctorListHeader className="txt-left">
                        <span className="header">
                          Consult any medical specialist
                        </span>
                      </DoctorListHeader>
                    }
                  />
                </DoctorListCompStyled>
              </div>
            </div>
          </div>
        </BlogPostBody>
      </div>
    </Suspense>
  );
};

export default BlogPost;

//
//HERO
//
export const SiteHeroBlog = styled(SiteHero)`
  background-image: url(${(props) => (props.LG ? props.LG : ImgBlogHero)});
  background-position: center-top;
  @media screen and (max-width: 992px) {
    background-image: url(${(props) => (props.MD ? props.MD : ImgBlogHero)});
  }
  @media screen and (max-width: 576px) {
    background-image: url(${(props) => (props.SM ? props.SM : ImgBlogHero)});
  }
`;

export const SiteHeroInner = styled.div`
  padding: 40px 0;
  text-align: left;
  color: #fff;

  .hero-featured {
    display: block;
    font-weight: bold;
    font-size: 18px;
    text-transform: uppercase;
    color: #fbe8d3;
  }

  h1 {
    font-weight: normal;
    font-size: 42px;
    margin: 0 0 50px;
  }

  p {
    font-weight: normal;
    font-size: 20px;
    margin: 0;
    line-height: 1.4;
  }

  .back-link {
    display: block;
    width: 70px;
    flex-grow: 0;
    padding: 5px 10px;
    border-radius: 30px;
    border: 1px solid #fff;
    color: #fff;
    text-align: center;
    font-size: 12px;
    margin-bottom: 100px;

    &:hover {
      background: #ffffff;
      color: #161836;
      text-decoration: none;
    }
  }
`;

export const SiteHeroActions = styled.div`
  display: flex;
  align-items: center;
  margin: 50px -10px 0;

  button {
    margin: 0 10px;
  }
`;

export const BlogPostBody = styled.div`
  padding: 60px 0;

  .blog-post-inner {
    @media screen and (min-width: 992px) {
      padding-right: 80px;
    }
  }

  h1,
  h2,
  h3,
  h4 {
    color: #10a6bd;
    font-weight: normal;
    line-height: 1.2;
    margin-bottom: 20px;
    font-size: 42px;
  }
  h2 {
    font-size: 32px;
  }

  h3 {
    font-size: 26px;
  }

  h4 {
    font-size: 18px;
  }

  p {
    color: #524e4e;
    margin: 0 0 20px;
    line-height: 1.4;
  }

  img {
    width: 100%;
    margin: 20px auto 40px;
  }
`;

//Related Posts
export const SBRelated = styled.div`
  .header {
    display: block;
    padding: 10px 0;
    margin-bottom: 40px;
    text-transform: uppercase;
    font-size: 16px;
    font-weight: 600;
    color: #10a6bd;
    border-bottom: 2px solid #10a6bd;
    cursor: pointer;
  }
`;

export const BlogPostGridItem = styled(Link)`
  display: block;
  background: #fff;
  border-radius: 5px;
  margin: 15px 5px 40px;

  &:hover {
    text-decoration: none;
  }
`;

export const BlogPostThumb = styled.div`
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  width: 100%;
  background-image: url(${(props) =>
    props.src ? props.src : ImgBlogPostThumb});
  background-size: cover;
  background-position: center center;
`;

export const BlogPostTags = styled.div`
  position: absolute;
  bottom: 15px;
  left: 15px;
  padding: 5px 10px;
  border-radius: 30px;
  border: 1px solid #fff;
  color: #fff;
  font-size: 12px;
`;

export const BlogPostContent = styled.div`
  padding: 20px 20px 0;

  h4 {
    font-size: 18px;
    color: #939495;
    font-weight: 600;
    margin-bottom: 20px;
  }
`;

export const BlogPostFooter = styled.div`
  padding: 20px 15px;
  color: #10a6bd;
  font-weight: 500;
  text-align: center;
  border-top: 1px solid #e8e8e8;
`;

//SB Widget
export const SBWidget = styled.div`
  .header {
    display: block;
    font-size: 28px;
    color: #10a6bd;
    font-weight: 400;
    margin-bottom: 20px;
  }

  .tags {
    padding: 0;
    margin: 0;

    li {
      .link {
        display: inline-block;
        flex-grow: 0;
        padding: 5px 10px;
        border-radius: 30px;
        border: 1px solid #9F9F9F;
        color: #9F9F9F;
        text-align: center;
        font-size: 14px;
        margin-bottom: 15px;    
    }
  }
`;

export const DoctorListCompStyled = styled(SBWidget)`
  background: red;

  .container {
    padding: 0 !important;
  }

  .doctorListSectionWrap {
    padding-top: 40px;
  }

  .selectContainerExpand {
    margin: 0 0 25px;
  }

  .drListItem {
    width: 25%;
    padding: 8px;
    margin: 0;

    img {
      width: 90%;
      height: auto;
      aspect-ratio: 1 / 1;
      margin: 0 auto;
      border-radius: 200px;
    }
  }
`;

export const DoctorListHeader = styled.div`
  text-align: left;
`;
